/**
 * Loads the landing page's 3D town for the login page.
 *
 * Two scripts, both outside the bundle: the scene taken from landing.html
 * (`src/landing/extractScene.js` — keep SCENE_SRC equal to its SCENE_PATH), then
 * the three.js build that script names. Each is fetched once per page load; a
 * later visit to the login page (after a logout) reuses them.
 */

export const SCENE_SRC = "/scene/wosool-scene.js";

// The first line extractScene writes. Anything else is not the scene.
const SCENE_HEADER = "/* Generated from src/landing/landing.html";

const pending = {};

/**
 * The scene is checked before it runs: a server that does not know the path
 * (a dev server started before setupProxy served it, or a deploy without the
 * file) answers with the SPA's index.html, and run as a script that is an
 * uncaught "Unexpected token '<'". Here it is a rejection, and the drawn map stays.
 */
function loadSceneScript() {
  if (!pending[SCENE_SRC]) {
    pending[SCENE_SRC] = fetch(SCENE_SRC)
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error(`${SCENE_SRC}: ${res.status}`))))
      .then((code) => {
        if (!code.startsWith(SCENE_HEADER)) throw new Error(`${SCENE_SRC} is not the scene script`);
        const el = document.createElement("script");
        el.textContent = code;
        document.head.appendChild(el);
      })
      .catch((e) => {
        // Not cached: the next visit to the login page tries again.
        delete pending[SCENE_SRC];
        throw e;
      });
  }
  return pending[SCENE_SRC];
}

function loadScript(src) {
  if (!pending[src]) {
    pending[src] = new Promise((resolve, reject) => {
      const el = document.createElement("script");
      el.src = src;
      el.async = true;
      el.onload = resolve;
      el.onerror = () => {
        // Not cached: the next visit to the login page tries again.
        delete pending[src];
        el.remove();
        reject(new Error(`Could not load ${src}`));
      };
      document.head.appendChild(el);
    });
  }
  return pending[src];
}

/** Resolves to `window.WOS`, with `WOS.bootWorld` ready to run. */
export async function loadScene() {
  if (!(window.WOS && window.WOS.bootWorld)) await loadSceneScript();
  if (!window.THREE) await loadScript(window.WOS_THREE);
  return window.WOS;
}

/**
 * Whether this visitor should get the 3D scene rather than the drawn map:
 * a wide screen (the scene's half is hidden below it), WebGL, and no
 * reduced-motion or data-saver preference.
 */
export function canShowScene() {
  const mq = (q) => window.matchMedia && window.matchMedia(q).matches;
  if (!mq("(min-width: 1024px)") || mq("(prefers-reduced-motion: reduce)")) return false;
  if (navigator.connection && navigator.connection.saveData) return false;
  try {
    const gl =
      document.createElement("canvas").getContext("webgl2") ||
      document.createElement("canvas").getContext("webgl");
    if (!gl) return false;
    const lose = gl.getExtension("WEBGL_lose_context");
    if (lose) lose.loseContext();
    return true;
  } catch (e) {
    return false;
  }
}
